import { createSelector } from "reselect";
import * as attachmentsSelectors from "store/selectors/input/attachments/attachmentsSelectors";

// A shared constant so that `attachedFileTrees` keeps a stable identity when
// nothing has been attached yet — `attachedFilesFlattened` memoises on it.
const NO_FILE_TREES = {};

export const treeIsLoaded = (state, fileId) => {
  const trees = state.temp?.attachments?.attachmentFileTrees || {};
  const tree = trees[fileId];
  if (!tree || tree.status === "LOADING") return false;
  // The tree is a tree of nodes where a node is
  // {
  // meta: FileMetaData,
  // child_metas: List[node]
  // }
  const ends = [
    "LOADED",
    "ERRORED",
    "NOT_VALID",
    "NOT_AVAILABLE",
    "TEXT_FILE",
    "CONVERSION_FAILED",
    "NOT_READABLE",
  ];
  const isMetaFinished = (meta) => {
    return (
      meta &&
      ends.includes(meta.status) &&
      ends.includes(meta.expansionStatus) &&
      ends.includes(meta.htmlStatus)
    );
  };
  const isComplete = (tree_entry) => {
    if (!tree_entry.meta) return false;
    if (!isMetaFinished(tree_entry.meta)) return false;
    for (const ind in tree_entry.childMetas) {
      if (!isComplete(tree_entry.childMetas[ind])) return false;
    }
    return true;
  };
  return isComplete(tree);
};

export const attachedFileTrees = (state) => {
  return state.temp?.attachments?.attachmentFileTrees || NO_FILE_TREES;
};

/** As `embeddedFiles`, but against an already selected tree map. Components
 * holding `attachedFileTrees` use this so they do not have to keep a
 * `state`-bound closure, which would change identity on every render.
 */
export const embeddedFilesIn = (fileTrees, fileId) => {
  const findFile = (entry) => {
    if (entry.meta?.id === fileId) return entry;
    const children = entry.childMetas || [];
    for (const ind in children) {
      const resp = findFile(children[ind]);
      if (resp) return resp;
    }
    return null;
  };
  const findRoot = () => {
    for (const id in fileTrees || {}) {
      const posRoot = findFile(fileTrees[id]);
      if (posRoot) return posRoot;
    }
    return null;
  };
  const root = findRoot() || {};
  const getEmbedded = (entry) => {
    if (!entry?.meta) return [];
    const res = [entry.meta.id];
    const children = entry.childMetas || [];
    for (const x in children)
      getEmbedded(children[x]).forEach((id) => res.push(id));
    return res;
  };
  const res = getEmbedded(root);
  const x = res && res.length > 0 ? res.slice(1) : [];
  return x;
};

export const embeddedFiles = (state, fileId) =>
  embeddedFilesIn(attachedFileTrees(state), fileId);

/* return a flattened list of {id: fileId, name: filename}
 * Hidden files are not included, files with orverridden
 * names have those names with the original extension if
 * no extension is present if a file is present with two names both entries are included*/
export const attachedFilesFlattened = createSelector(
  [
    attachmentsSelectors.getFlattenedFileNameOverrides,
    attachmentsSelectors.getFlattenedHiddenFiles,
    attachedFileTrees,
  ],
  (names, hidden, fileTrees) => {
    const res = [];
    const add = (entry) => {
      if (entry?.meta) {
        const id = entry.meta.id;
        if (!hidden[id]) {
          const getName = () => {
            const name = names[id];
            if (!name) return entry.meta.originalFilename;
            const ind = name.lastIndexOf(".");
            if (ind > 0 && ind > name.length - 10 && ind < name.length - 1) {
              return name;
            }
            return name + "." + entry.meta.originalExtension;
          };
          res.push({ id, name: getName() });
          if (entry.childMetas) {
            entry.childMetas.forEach((c) => add(c));
          }
        }
      }
    };
    Object.values(fileTrees).forEach((e) => add(e));
    return res;
  }
);

export const isSelectingAttachments = (state) =>
  state.temp.attachments?.selectingAttachment ?? false;
